import * as calc from "./walletOperations";
import events from "../events/EventsObservable";
import { GAME_EVENTS } from "../game/gameEvents";
import InitResult from "../connect/result/InitResult";
import InitWallet from "./InitWallet";
import { getClosestValue, numberFormat } from "../helpers";
import {
	calcCoinValue,
	calcReal,
	COIN_DIVISOR,
	getCoinValue,
	setCoinDivisor,
} from "./walletOperations";
import { inject, injectable } from "inversify";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import SystemDebug from "../system/SystemDebug";
import Settings from "../settings/Settings";
import SystemConfig, { CONFIG_PARAMS, MODES } from "../system/SystemConfig";
import Connect from "../connect/Connect";
import StatesManager from "../states/StatesManager";
import { STATES } from "../states/states";
import AddResult from "../connect/result/AddResult";
import PlatformSettings from "../connect/result/PlatformSettings";
import { MESSAGE_EVENTS } from "../error/errorMessages";

export const WALLET_EVENTS = {
	WALLET_ON_INITED: "WALLET_ON_INITED",
	WALLET_ON_SET_BALANCE: "WALLET_ON_SET_BALANCE",
	WALLET_ON_SET_BET: "WALLET_ON_SET_BET",
	WALLET_ON_SET_LINES_COUNT: "WALLET_ON_SET_LINES_COUNT",
	WALLET_ON_SET_FREESPINS: "WALLET_ON_SET_FREESPINS",
	WALLET_ON_SET_WIN: "WALLET_ON_SET_WIN",
	WALLET_ON_SET_COIN: "WALLET_ON_SET_COIN",
	WALLET_ON_TOTAL_LOSS: "WALLET_ON_TOTAL_LOSS",
};

@injectable()
export default class Wallet {
	private _fsTotalWin?: number | null;
	getFSTotalWin(): number {
		return this._fsTotalWin ?? 0
	}
	setFSTotalWin(val?:number|null) {
		this._fsTotalWin = val
	}
	private debug: SystemDebug;
	private config: SystemConfig;
	private connect: Connect;
	private states: StatesManager;
	
	private _linesCount: number = 1;
	private _lines: Array<number>;
	
	private _currency: string = "CENTS";
	
	private _decimalPlaces: number = 0;
	private _creditWinDecimalPlaces: number | undefined = undefined;
	private _realWinDecimalPlaces: number | undefined = undefined;
	
	private _creditBets: Array<number> = [];
	private _fsBets: Array<number> = []
	private _betSet: Array<number> = []; //[10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 120, 140, 150, 160, 180, 200, 250, 300, 350, 400, 450, 500,…]

	private _coin: number = COIN_DIVISOR;
	private _coins: Array<number> = [];
	private _fsCoins?: number[];

	private _balance: number = 0;
	private _win: number = 0;
	private _bet: number = 0;

	private _bonus_bet: number;
	private _freespins: number = 0;
	private _is_bonus: boolean;
	private _valid_bonus_bet: boolean;
	private _realBetSnapshot: { bet: number; coin: number; linesCount: number } | null = null;

	private _max_bet: number;
	private _max_line_bet: number;
	private _min_bet: number;
	private _min_line_bet: number;

	private _actualBalance: number;
	private _totalLoss: number;

	private _balanceInterval: any; // interval for fetching balance when no credit
	private _tmpBallance: number | null = null;

	private _fsBetSet?: number[];
	private _fsMinBet?: number;

	constructor(
		@inject(SERVICE_IDENTIFIER.SystemDebug) debug: SystemDebug,
		@inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
		@inject(SERVICE_IDENTIFIER.Connect) connect: Connect,
		@inject(SERVICE_IDENTIFIER.StatesManager) states: StatesManager
	) {
		this.debug = debug;
		this.config = config;
		this.connect = connect;
		this.states = states;

		this._lines = [this._linesCount];

		// events.subscribe(ERROR_EVENTS.ERROR_BALANCE_REFUND, this);
		events.subscribe(GAME_EVENTS.GAME_START_ROUND, this);
		events.subscribe(GAME_EVENTS.GAME_END_ROUND, this);
		events.subscribe(WALLET_EVENTS.WALLET_ON_SET_BALANCE, this);
	}

	notify(eventName, data) {
		switch (eventName) {
			// case ERROR_EVENTS.ERROR_BALANCE_REFUND:
			//     this._refund(data);
			//     break;

			case GAME_EVENTS.GAME_START_ROUND:
				this._gameStartRound();
				break;

			case GAME_EVENTS.GAME_END_ROUND:
				this._gameEndRound();
				break;
			case WALLET_EVENTS.WALLET_ON_SET_BALANCE:
				this._onSetBalance();
				break;
		}
	}

	async _dispatch(type, data?) {
		return events.dispatch(type, data);
	}

	async initSettings(
		settings: Settings,
		platformSettings: PlatformSettings,
		initWallet: InitWallet
	) {
		this._decimalPlaces = settings.getDecimalPlaces() ?? 2;
		setCoinDivisor(10 ** this._decimalPlaces);
		this._betSet = settings.getBetSet(); // real money
		this._fsBetSet = settings.getFSBetSet();
		this._coins = settings.getCoins()
			? settings.getCoins()
			: [10, 20, 50, 100, 200, 500, 1000];
		this._fsCoins = settings.getFsCoins();
		this._creditBets = initWallet.getCreditBets();
		this._fsBets = settings.getFsCreditBets() ?? this._creditBets

		this._max_bet = settings.getMaxBet();
		this._max_line_bet = settings.getMaxLineBet();
		this._min_bet = settings.getMinBet();
		this._fsMinBet = settings.getFsMinBet();
		this._min_line_bet = settings.getMinLineBet();

		// Override data with platform settings
		if (platformSettings !== undefined) {
			this._betSet =
				platformSettings.getBetSet()?.length > 0
					? platformSettings.getBetSet()
					: this._betSet;
			this._max_bet = platformSettings.getMaxBet()
				? platformSettings.getMaxBet()
				: this._max_bet;
			this._max_line_bet = platformSettings.getMaxLineBet()
				? platformSettings.getMaxLineBet()
				: this._max_line_bet;
			this._min_bet = platformSettings.getMinBet()
				? platformSettings.getMinBet()
				: this._min_bet;
			this._min_line_bet = platformSettings.getMinLineBet()
				? platformSettings.getMinLineBet()
				: this._min_line_bet;
			platformSettings?.getAutostart() &&
				this.config.set(CONFIG_PARAMS.AUTOSTART, {
					values: platformSettings.getAutostart(),
				});
		}

		this._initLines(settings.getLines(), settings.getLinesCount());
		// set lines and default lines count from settings, if not set, set default values (1)

		// set bet and coin from settings
		this._coin = getClosestValue(this._coins, settings.getCoin());

		let defaultBet = getClosestValue(
			this.getAllowedBets(),
			platformSettings?.getBet() || settings.getBet()
		);

		if (
			settings.getCreditOrder() === "bonus_first" &&
			this.config.get(CONFIG_PARAMS.MODE) === MODES.REAL
		) {
			this.setIsBonus(true);
		} else {
			this.setIsBonus(false);
		}

		// Is bet from settings in betset and in bounds? if no, pick nearest value form allowed betset values
		if (!this.isBetInBounds(defaultBet)) {
			defaultBet = getClosestValue(this.getAllowedBets(), defaultBet);
			console.warn(
				`Settings.bet ` +
					settings.getBet() +
					` is not valid. Using bet ` +
					defaultBet
			);
		}

		let data = this.calcCoinFromBet(defaultBet);
		this._coin = data.coin;
		this._bet = calc.getMoneyBet(
			data.creditBet,
			this._coin,
			this._decimalPlaces
		);

		if (
			settings.getActions()?.balance?.repeat &&
			this.config.get(CONFIG_PARAMS.MODE) !== MODES.REPLAY
		) {
			setInterval(() => {
				try {
					this.connect.balance();
				} catch (e) {
					console.log(e);
				}
			}, settings.getActions().balance.repeat * 1000);
		}
	}

	async init(
		initResult: InitResult,
		platformSettings: PlatformSettings,
		initWallet: InitWallet
	) {
		let lastRound = initResult.getLastRound();
		if (lastRound && !lastRound.ended && lastRound.linesCount != null) {
			this._linesCount = lastRound.linesCount;
		}

		this._currency = initResult.getCurrency();
		this._balance = initResult.getBalance();

		if (lastRound && !lastRound.ended) {
			if (lastRound.coin != null) {
				this._coin = lastRound.coin;
			}
			if (lastRound.atomicBet != null) {
				this._bet = lastRound.atomicBet;
			}
		}

		if (lastRound && !lastRound.ended && lastRound.atomicBet != null) {
			this._bet = lastRound.atomicBet;
		}

		let minCoinValue = calc.calcCoinValue(this._coins[0], this._decimalPlaces);
		let minCreditBet = this._creditBets[0];
		let minRealBet = calc.calcReal(this._betSet[0], this._decimalPlaces);

		// if(minCoinValue * minCreditBet != minRealBet) {
		//     console.warn(`Error: wrong coinValues or betset: ${minCoinValue}*${minCreditBet}!=${minRealBet}`);
		// }

		if (lastRound && !lastRound.ended && lastRound.atomicBet != null) {
			this._balance += lastRound.atomicBet * this._linesCount;
		}

		// Set bonus bet and rounds
		if (initResult?.getBonus()) {
			this.setBonusData(initResult.getBonus(), true);
		}
		this._fsTotalWin = initResult.getFSTotalWin()

		// Override bonus bet from platform settings
		if (platformSettings?.getBonusBet()) {
			this.setBonusData(
				{ bet: platformSettings.getBonusBet(), rounds: this.getFreeSpins() },
				true
			);
		}

		if (
			!(this.getFreeSpins() > 0) ||
			!this.getBonusBet() ||
			(this.getBonusBet() && !this.isValidBonusBet())
		) {
			this.setIsBonus(false);
		}

		await this._dispatch(WALLET_EVENTS.WALLET_ON_INITED);

		this.recalc();

		let walletData = this._getWalletData();

		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_BALANCE, walletData);
		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_BET, walletData);
		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_WIN, walletData);
	}

	async initReplay(
		replayResult: AddResult,
		settings: Settings,
		initWallet: InitWallet
	) {
		this._balance = this.creditToMoney(replayResult.getBalance());
		this._bet = replayResult.getBet() / replayResult.getLinesCnt();
		this._coin = replayResult.getCoin();
		this._linesCount = replayResult.getLinesCnt();
		this._decimalPlaces = settings.getDecimalPlaces() ?? 2;
		setCoinDivisor(10 ** this._decimalPlaces);
		this._currency = this.config.get(CONFIG_PARAMS.CURRENCY);
		this.recalc();

		await this._dispatch(WALLET_EVENTS.WALLET_ON_INITED);

		let walletData = this._getWalletData();

		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_BALANCE, walletData);
		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_BET, walletData);
		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_WIN, walletData);
	}

	_initLines(lines: Array<number> | undefined, lines_count: number) {
		if (lines === null) {
			this._lines = [1];
			this._linesCount = this._lines.length;
		} else {
			this._lines = lines;
			if (lines_count) {
				if (!this._lines.includes(lines_count)) {
					this._linesCount = getClosestValue(this._lines, lines_count);
				} else {
					this._linesCount = lines_count;
				}
			} else {
				this._linesCount = 1;
			}
		}
	}

	recalcBets({
		creditBet = this.getCreditBet(),
		coinValue = this.getCoinValue(),
		linesCount = this.getLinesCount(),
	}) {
		let coin = calc.calcCoin(coinValue);

		let allowedCreditBets = this.getAllowedCreditBets(coin);

		return {
			creditBets: this.getCreditBets(),
			creditBet: creditBet,
			allowedCreditBets: allowedCreditBets,
			CoinValues: this.getCoinValues(),
			coinValue: coinValue,
			allowedCoinValues: this.getAllowedCoinValues(creditBet),
			lines: this.getLines(),
			linesCount: linesCount,
		};
	}

	_getAllowedCoins(creditBet = this.getCreditBet()) {
		let coins = [];

		for (let coin of this._getCoins()) {
			let atomBet = calc.getMoneyBet(creditBet, coin, this._decimalPlaces);
			this.isBetInBounds(atomBet) && coins.push(coin);
		}

		return coins;
	}

	getAllowedCoinValues(creditBet = this.getCreditBet()) {
		return calc.calcCoinValues(
			this._getAllowedCoins(creditBet),
			this._decimalPlaces
		);
	}

	getAllowedBets() {
		let bets = [];
		for (let line of this.getLines()) {
			for (let bet of this.getCreditBets()) {
				for (let coin of this.getCoins()) {
					let realBet = bet * coin;
					if (this.isBetInBounds(realBet, line) && !bets.includes(realBet)) {
						bets.push(realBet);
					}
				}
			}
		}
		bets.sort((a, b) => {
			if (a > b) return 1;
			if (a < b) return -1;
			return 0;
		});
		return bets;
	}

	getAllowedCreditBets(coin = this.getCoin()): Array<number> {
		let bets = [];

		for (let creditBet of this.getCreditBets()) {
			let atomBet = calc.getMoneyBet(creditBet, coin, this._decimalPlaces);
			this.isBetInBounds(atomBet) && bets.push(creditBet);
		}

		return bets;
	}

	getCreditBets(){
		return this.isBonus() ? this._fsBets : this._creditBets
	}

	/**
	 * Function returns allowed coin and creditBet for given bet.
	 * @param bet - bet in money (bet = 100  -> 1eur)
	 * @param linesCount - linesCount, current linesCount is used by default
	 */
	calcCoinFromBet(bet: number, linesCount: number = this._linesCount) {
		for (let creditBet of this.getCreditBets()) {
			for (let coin of this._getCoins()) {
				if (
					bet * linesCount === coin * creditBet * linesCount &&
					this.isBetInBounds(coin * creditBet, linesCount)
				) {
					return { coin, creditBet };
				}
			}
		}
		this.dumpData();
		throw (
			"Error: Unable to find coin and creditBet combination for bet value: " +
			bet
		);
	}

	getCoinValue(coin = this._coin) {
		return calcCoinValue(coin, this._decimalPlaces);
	}

	getCoinValues() {
		return calc.calcCoinValues(this._coins, this._decimalPlaces);
	}

	getLines(): Array<number> {
		return this._lines;
	}

	/**
	 * Return current bet including lines.
	 */
	getTotalBet() {
		return calc.getBet(this._bet, this._linesCount);
	}

	getBetSet() {
		return this._getBetSet();
	}

	getTotalLoss() {
		return this._totalLoss;
	}

	/**
	 * @todo
	 */
	getBetIndex() {
		let bet = this.getBet();
		return this.getBetSet().indexOf(bet);
	}

	getWin() {
		return this._win;
	}

	getRealWin() {
		return calc.getReal(this._win, this._decimalPlaces);
	}

	getLinesCount() {
		return this._linesCount;
	}

	getFreeSpins() {
		return this._freespins;
	}

	setFreeSpins(val: number) {
		this._freespins = val;
		this._dispatch(
			WALLET_EVENTS.WALLET_ON_SET_FREESPINS,
			this._getWalletData()
		);
	}

	getMaxBet() {
		return this._max_bet;
	}

	getMaxLineBet() {
		return this._max_line_bet;
	}

	getMinBet() {
		return this._min_bet;
	}

	getMinLineBet() {
		return this._min_line_bet;
	}

	setIsBonus(val: boolean) {
		this._is_bonus = val;
	}

	isBonus() {
		return this._is_bonus;
	}

	setBonusData(bonus: any, isInit: boolean = false) {
		// save last bonus bet value
		if (bonus?.bet) {
			if (bonus.bet !== this._bonus_bet) {
				this._bonus_bet = bonus.bet;
				this._valid_bonus_bet = this.checkBonusBet(this.getBonusBet(), isInit);
			}
		}
		// update freespins value, if 0 turn off bonus
		if (bonus?.rounds > 0) {
			this.setFreeSpins(bonus.rounds);
		} else {
			this.setFreeSpins(0);
			// this.setIsBonus(false);
		}
		// if in bonus, set bet to saved value
		if (
			this.isBonus() &&
			this.getBet() * this.getLinesCount() !== this.getBonusBet() &&
			this.isValidBonusBet()
		) {
			this.setBonusBet();
		}
	}

	setBonusBet() {
		if (this.isBonus()) {
			let data = this.calcDataFromBet(this._bonus_bet);
			this._coin = data.coin;
			this._linesCount = data.linesCount;
			this.setBet(Math.round(this._bonus_bet / this._linesCount));
		}
	}

	snapshotRealBet() {
		if (this.isBonus()) return;
		this._realBetSnapshot = { bet: this.getBet(), coin: this.getCoin(), linesCount: this.getLinesCount() };
	}

	/** Expects setIsBonus(false) already called. */
	restoreRealBet() {
		const snap = this._realBetSnapshot;
		this._realBetSnapshot = null;
		if (snap && this.isBetInBounds(snap.bet, snap.linesCount)) {
			this._applyRealBet(snap.bet, snap.coin, snap.linesCount);
			return;
		}
		if (this.isBetInBounds(this.getBet())) return;
		const target = getClosestValue(this.getAllowedBets(), this.getBet());
		// not calcDataFromBet(): it validates against the current (possibly fs) coin and throws
		for (const linesCount of this._lines) {
			for (const creditBet of this._creditBets) {
				for (const coin of this._coins) {
					if (coin * creditBet === target && this.isBetInBounds(target, linesCount)) {
						this._applyRealBet(target, coin, linesCount);
						return;
					}
				}
			}
		}
		console.error("Wallet.restoreRealBet: no real bet combination found for", target, "keeping", this.getBet());
		this.dumpData();
	}

	private _applyRealBet(bet: number, coin: number, linesCount: number) {
		this._coin = coin;
		this._linesCount = linesCount;
		this.setBet(bet);
	}

	getBonusBet() {
		return this._bonus_bet;
	}

	checkBonusBet(
		bonus_bet: number = this.getBonusBet(),
		isInit: boolean = false
	) {
		for (let linesCount of this._lines) {
			for (let creditBet of this._fsBets ?? []) {
				for (let coin of this._fsCoins) {
					if (
						bonus_bet === coin * creditBet * linesCount &&
						this.isBetInBounds(
							coin * creditBet,
							linesCount,
							this._fsBetSet,
							this._fsMinBet
						)
					) {
						!isInit && events.dispatch(GAME_EVENTS.GAME_ALLOW_FREESPINS);
						return true;
					}
					// if (
					// 	bonus_bet === coin * creditBet * linesCount &&
					// 	this.isBetInBounds(coin * creditBet, linesCount, this._fsBetSet)
					// ) {
					// 	!isInit && events.dispatch(GAME_EVENTS.GAME_ALLOW_FREESPINS);
					// 	return true;
					// }
				}
			}
		}
		console.warn("Warning: invalid value _bonus_bet: ", bonus_bet);
		this.isBonus() && events.dispatch(MESSAGE_EVENTS.SWITCH_TO_REAL);
		return false;
	}

	isValidBonusBet() {
		return this._valid_bonus_bet;
	}

	private _getCoins() {
		if (this.isBonus()) return this._fsCoins ?? this._coins;
		return this._coins;
	}

	/**
	 * Function returns allowed coin, linesCount and creditBet for given bet.
	 * @param bet - bet in money (bet = 100  -> 1eur) (with linesCount included)
	 */
	calcDataFromBet(bet: number) {
		for (let linesCount of this._lines) {
			for (let creditBet of this.getCreditBets()) {
				for (let coin of this._getCoins()) {
					if(bet !== coin * creditBet * linesCount){
						console.debug(`lines: ${linesCount}, credit: ${creditBet}, coin: ${coin} does not compute to ${bet}`)
						continue
					}
					
					if(!this.isBetInBounds(coin * creditBet, linesCount)){
						console.debug(`lines: ${linesCount}, bet: ${coin * creditBet} is not in bounds`)
						continue
					}
					
					if(!this.isBetInBounds(coin * creditBet, linesCount)){
						console.debug(`lines: ${linesCount}, bet: ${coin * creditBet} is not in bounds`)
						continue
					}
					
					if(!this._canSetCreditBet(creditBet, coin)){
						console.debug(`cant set credit bet for credit: ${creditBet}, coin: ${coin}`)
						continue
					}
					
					if (
						!this._canSetCoin(coin, creditBet)
					) {
						console.debug(`cant set credit coin for credit: ${creditBet}, coin: ${coin}`)
						continue
					}
					return { coin, creditBet, linesCount };
				}
			}
		}
		this.dumpData();
		throw (
			"Error: Unable to find coin and creditBet combination for bet value: " +
			bet
		);
	}

	dumpData() {
		console.error("Data dump: ", {
			bet_set: this.getBetSet(),
			coins: this.getCoins(),
			bet_values: this.getCreditBets(),
			line_set: this.getLines(),
			lines_count: this.getLinesCount(),
			is_bonus: this.isBonus(),
			freespins: this.getFreeSpins(),
			bonus_bet: this.getBonusBet(),
		});
	}

	/**
	 * Return current bet without linesCount
	 */
	getBet() {
		return this._bet;
	}

	setBet(bet) {
		this._bet = bet;
		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_BET, this._getWalletData());
	}

	getCoin() {
		return this._coin;
	}

	getCoins() {
		return this._coins;
	}

	getCreditBet() {
		return calc.moneyToCredit(this._bet, this._coin, this._decimalPlaces);
	}

	getCreditBetLines() {
		return this.getCreditBet() * this._linesCount;
	}

	getRealBet(bet = this._bet) {
		return calc.getRealBet(bet, this._linesCount, this._decimalPlaces);
	}

	getDecimalPlaces() {
		return this._decimalPlaces;
	}

	setWinDecimalPlaces(win: number) {
		this._creditWinDecimalPlaces = numberFormat(
			this.moneyToCreditWithDecimals(win),
			this.getDecimalPlaces(),
			".",
			" "
		).split(".")[1].length;
		this._realWinDecimalPlaces = numberFormat(
			calcReal(win, this.getDecimalPlaces()),
			this.getDecimalPlaces(),
			".",
			" "
		).split(".")[1].length;
	}

	getCreditWinDecimalPlaces(): number | undefined {
		return this._creditWinDecimalPlaces;
	}

	getRealWinDecimalPlaces(): number | undefined {
		return this._realWinDecimalPlaces;
	}

	getCurrency() {
		if (this._currency === "CENTS") {
			return "";
		}

		return this._currency;
	}

	setLinesCount(num, force = false) {
		if (this._linesCount === num) {
			return true;
		}

		if (force || this._canSetLinesCount(num)) {
			this._linesCount = num;
			this._dispatch(
				WALLET_EVENTS.WALLET_ON_SET_LINES_COUNT,
				this._getWalletData()
			);
			return true;
		}

		return false;
	}

	_canSetLinesCount(num) {
		return this._lines.includes(num);
	}

	setCoin(coin, creditBet = this.getCreditBet(), force = false) {
		if (force || this._canSetCoin(coin, creditBet)) {
			this._coin = coin;

			let bet = calc.getMoneyBet(creditBet, coin, this.getDecimalPlaces());
			this.setBet(bet);

			this._dispatch(WALLET_EVENTS.WALLET_ON_SET_COIN, this._getWalletData());
			return true;
		}

		return false;
	}

	_canSetCoin(coin, creditBet = this.getCreditBet()) {
		if (!this._getAllowedCoins(creditBet).includes(coin)) {
			console.log(`Coin ${coin} not allowed`);
			return false;
		}
		return true;
	}

	setCreditBet(creditBet, coin = this.getCoin(), force = false) {
		let atomBet = calc.getMoneyBet(creditBet, coin, this.getDecimalPlaces());

		if (!force && this._bet == atomBet) {
			return true;
		}

		if (force || this._canSetCreditBet(creditBet, coin)) {
			this.setBet(atomBet);
			return true;
		}

		return false;
	}

	_canSetCreditBet(creditBet, coin = this.getCoin()) {
		return this.getAllowedCreditBets(coin).includes(creditBet);
	}
	private _getBetSet() {
		if (this.isBonus()) return this._fsBetSet ?? this._betSet;
		return this._betSet;
	}
	private _getMinBet() {
		if (this.isBonus()) return this._fsMinBet ?? this._min_bet;
		return this._min_bet;
	}
	isBetInBounds(
		bet: number,
		line: number = this.getLinesCount(),
		betSet: number[] = this._getBetSet(),
		minBet = this._getMinBet()
	): boolean {
		// if game has 1 line setting, ignore max/min line bet
		if (this.getLines().length > 1) {
			// is bet in betset and is bet lower than max_bet or max_line_bet
			// and is bet higher than min_ber and min_line_bet
			return (
				betSet.includes(bet) &&
				bet * line >= this._min_bet &&
				bet >= this._min_line_bet &&
				bet * line <= this._max_bet &&
				bet <= this._max_line_bet
			);
		} else {
			// is bet in betset and is bet lower than max_bet and is bet higher than min_bet
			return betSet.includes(bet) && bet >= minBet && bet <= this._max_bet;
		}
	}

	updateWallet(
		creditBet,
		coinValue = this.getCoinValue(),
		linesCount = this.getLinesCount()
	) {
		let coin = calc.getCoin(coinValue);

		if (!this._canSetLinesCount(linesCount)) return false;
		if (!this._canSetCoin(coin, creditBet)) return false;
		if (!this._canSetCreditBet(creditBet, coin)) return false;

		this.setCoin(coin, creditBet, true);
		this.setCreditBet(creditBet, this.getCoin(), true);
		this.setLinesCount(linesCount, true);

		return true;
	}

	setWin(val) {
		this._win = val;
		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_WIN, this._getWalletData());
	}

	async setWinAsync(val) {
		this._win = val;
		await this._dispatch(
			WALLET_EVENTS.WALLET_ON_SET_WIN,
			this._getWalletData()
		);
	}

	addWin(val) {
		this.setWin(this.getWin() + val);
	}

	async addWinAsync(val) {
		await this.setWinAsync(this.getWin() + val);
	}

	getBalance() {
		return this._balance;
	}

	setBalance(val: number) {
		this._balance = val;
		this._dispatch(WALLET_EVENTS.WALLET_ON_SET_BALANCE, this._getWalletData());
	}

	setBalanceSilent(val: number) {
		this._balance = val;
	}

	getCreditBalance() {
		return calc.moneyToCredit(this._balance, this._coin, this._decimalPlaces);
	}

	getRealBalance() {
		return calc.getReal(this._balance, this._decimalPlaces);
	}

	getRealFsTotalWin(){
		return calc.getReal(this.getFSTotalWin(), this._decimalPlaces);
	}

	addWinToBalance() {
		this.setBalanceSilent(this.getBalance() + this.getWin());
	}

	updateBalanceUI() {
		return this._dispatch(
			WALLET_EVENTS.WALLET_ON_SET_BALANCE,
			this._getWalletData()
		);
	}

	removeBet() {
		if (this.isBonus()) {
			this.setFreeSpins(this.getFreeSpins() - 1);
		} else {
			this.setBalance(this.getBalance() - this.getTotalBet());
		}
	}

	creditToMoney(credit) {
		return calc.creditToMoney(credit, this.getCoin(), this.getDecimalPlaces());
	}

	moneyToCredit(money) {
		return calc.moneyToCredit(money, this.getCoin(), this.getDecimalPlaces());
	}

	moneyToCreditWithDecimals(money) {
		return calc.moneyToCreditWithDecimals(
			money,
			this.getCoin(),
			this.getDecimalPlaces()
		);
	}

	checkBalance() {
		if (this.getBalance() >= this.getTotalBet()) {
			this._clearBalanceInterval();
		} else {
			this._noCredit();
		}
		if (this.isBonus()) {
			return this.getFreeSpins() > 0;
		} else {
			return this.getBalance() >= this.getTotalBet();
		}
	}

	private _getWalletData(): WalletData {
		let currency = this.getCurrency();
		let decimals = this.getDecimalPlaces();
		let realWinDecimals = this.getRealWinDecimalPlaces()
			? this.getRealWinDecimalPlaces()
			: decimals;

		let params: WalletData = {
			linesCount: this.getLinesCount(),
			creditBalance: this.getCreditBalance(),
			creditBetAtomic: this.getCreditBet(),
			creditBetLines: this.getCreditBetLines(),
			// creditAtomicBet: this.getCreditAtomicBet(),
			creditWin: this.moneyToCredit(this.getWin()),
			freespins: this.getFreeSpins(),

			win: this.getWin(),
			atomicBet: this.getBet(),

			realBalanceCurrency: calc.withCurrency(
				Number(this.getRealBalance()),
				currency,
				decimals
			),
			realBetCurrency: calc.withCurrency(
				Number(this.getRealBet()),
				currency,
				decimals
			),
			realWinCurrency: calc.withCurrency(
				Number(this.getRealWin()),
				currency,
				realWinDecimals
			),

			coin: this.getCoin(),
			coinValue: this.getCoinValue(),
		};

		// remove currency in free mode
		// do not remove in portugal legislation
		if (
			this.config.get(CONFIG_PARAMS.MODE) === MODES.FREE &&
			!(this.config.get(CONFIG_PARAMS.LEGISLATION) === "pt")
		) {
			params.realBalanceCurrency = calc.withCurrency(
				Number(this.getRealBalance()),
				"",
				decimals
			);
			params.realBetCurrency = calc.withCurrency(
				Number(this.getRealBet()),
				"",
				decimals
			);
			params.realWinCurrency = calc.withCurrency(
				Number(this.getRealWin()),
				"",
				realWinDecimals
			);
		}
		return params;
	}

	refund(balance: number) {
		if (this.isBonus()) {
			this.setFreeSpins(this.getFreeSpins() + 1);
		} else {
			if (balance != null) {
				this.setBalance(balance);
			}
		}
	}

	_gameStartRound() {
		this.refreshBalance(null);
		if (this._totalLoss != null) {
			this.refreshTotalLoss(this._totalLoss + this.getTotalBet());
			this.computeTotalLoss();
		} else {
			this.refreshTotalLoss(null);
		}
	}

	refreshBalance(balance: number) {
		if (this.states.getState() === STATES.IDLE && balance != null) {
			this.setBalance(balance);
		} else {
			this._tmpBallance = balance;
		}
	}

	refreshTotalLoss(totalLoss: number) {
		this._totalLoss = totalLoss;
	}
	private _onSetBalance() {
		if (this._tmpBallance !== null) {
			this.setBalance(this._tmpBallance);
			this._tmpBallance = null;
		}
	}

	_gameEndRound() {
		if (this.debug.dbgWallet) {
			console.log(
				"refresing wallet balance (WS): ",
				this._actualBalance,
				this._totalLoss
			);
		}

		if (this._actualBalance != null) {
			this.setBalance(this._actualBalance);
		}

		if (this._totalLoss != null) {
			this.refreshBalance(null);
			this.refreshTotalLoss(this._totalLoss - this.getWin());
			this.computeTotalLoss();
		}
	}

	_noCredit() {
		if (!this._balanceInterval) {
			let time = this.config.get(CONFIG_PARAMS.TIMEOUTS).balance / 2;
			this._balanceInterval = setInterval(
				this._fetchBalance.bind(this),
				Number(time * 1000)
			);
		}
	}

	_fetchBalance() {
		try {
			this.connect.balance().then((res) => {
				if (res.data && res.data.success && res.data.success === 1) {
					const hasBalance = !!res.data.balance;
					if (hasBalance) {
						this.setFSTotalWin(res.data.fs_total_win)
						this.setBalance(res.data.balance);
					}
					this._applyBalanceBonus(res.data.bonus);
					if (hasBalance) this.checkBalance();
				}
			});
		} catch (e) {
			console.log(e);
		}
	}

	/**
	 * KAJOT-79: free spins granted while the game is open (Playtech FSB accepted in the wrapper,
	 * then w2gRefreshBalanceCommand) arrive in the balance response. Applied in IDLE only; a
	 * running cycle picks them up from the end-round response as before. `null` is ignored on
	 * purpose: dropping an active campaign stays an end-round decision.
	 */
	private _applyBalanceBonus(bonus: { rounds: number; bet: number } | null | undefined) {
		if (!bonus || this.states.getState() !== STATES.IDLE) return;
		const currentFs = this.getFreeSpins();
		if (bonus.rounds === currentFs && bonus.bet === this.getBonusBet()) return;
		// setBonusData first: dispatch runs subscribers synchronously and Freespins checks isValidBonusBet()
		this.setBonusData(bonus);
		if (bonus.rounds > currentFs) {
			events.dispatch(GAME_EVENTS.GAME_NEW_FREESPINS, { rounds: bonus.rounds - currentFs });
		}
	}

	reloadBalance() {
		this._fetchBalance();
	}

	_clearBalanceInterval() {
		if (this._balanceInterval) clearInterval(this._balanceInterval);
		this._balanceInterval = undefined;
	}

	computeTotalLoss() {
		let currency = this.getCurrency();
		let decimals = this.getDecimalPlaces();

		let loss = this._totalLoss;
		let lossWithCurrency = calc.withCurrency(
			calc.moneyToReal(loss, decimals),
			currency,
			decimals
		);

		if (this.debug.dbgWallet) {
			console.log("total loss (WS): ", loss, lossWithCurrency);
		}

		this._dispatch(WALLET_EVENTS.WALLET_ON_TOTAL_LOSS, {
			loss,
			lossWithCurrency,
		});
	}

	public setMaxBet() {
		let balance = this.getBalance();
		let lines = this.getLines();
		let decimals = this.getDecimalPlaces();
		let creditBets = this.getCreditBets();
		let coinValues = this.getCoinValues();
		let betset = this.getBetSet();

		let maxbet = this.calcMaxBet();

		// console.log("Setting max bet: ", maxbet);

		this.updateWallet(maxbet.creditBet, maxbet.coinValue, maxbet.linesCount);
	}

	recalc() {
		// console.log("Recalculatig bet ?", !(this.getBalance() >= this.getTotalBet()));

		if (
			this.isBonus() ||
			(!this.isBonus() && this.getBalance() >= this.getTotalBet())
		) {
			return;
		}

		console.log(
			"wallet recalculatig...",
			this.getBalance(),
			this.getTotalBet()
		);

		let maxbet = this.calcMaxBet();

		console.log(maxbet);
		this.updateWallet(maxbet.creditBet, maxbet.coinValue, maxbet.linesCount);
	}

	incCreditBet() {
		let creditBet = this.nextCreditBet();
		if (creditBet != null) {
			return this.setCreditBet(creditBet);
		}
		return false;
	}

	nextCreditBet() {
		let actualCb = this.getCreditBet();
		let creditBets = this.getCreditBets();
		let index = creditBets.indexOf(actualCb);
		let allowedCreditBets = this.getAllowedCreditBets();

		for (let i = index + 1; i < creditBets.length; i++) {
			let cb = creditBets[i];

			if (allowedCreditBets.includes(cb) && this._canSetCreditBet(cb)) {
				return cb;
			}
		}
		return null;
	}

	decCreditBet() {
		let creditBet = this.prevCreditBet();
		if (creditBet != null) {
			return this.setCreditBet(creditBet);
		}
		return false;
	}

	prevCreditBet() {
		let actualCb = this.getCreditBet();
		let creditBets = this.getCreditBets();
		let index = creditBets.indexOf(actualCb);
		let allowedCreditBets = this.getAllowedCreditBets();

		for (let i = index - 1; i >= 0; i--) {
			let cb = creditBets[i];
			if (allowedCreditBets.includes(cb) && this._canSetCreditBet(cb)) {
				return cb;
			}
		}
		return null;
	}

	incCoin() {
		let actualCoin = this.getCoin();
		let coins = this._coins;
		let index = coins.indexOf(actualCoin);
		let allowedCoins = this._getAllowedCoins();

		for (let i = index + 1; i < coins.length; i++) {
			let coin = coins[i];
			if (allowedCoins.includes(coin)) {
				return this.setCoin(coin);
			}
		}
		return false;
	}

	decCoin() {
		let actualCoin = this.getCoin();
		let coins = this._coins;
		let index = coins.indexOf(actualCoin);
		let allowedCoins = this._getAllowedCoins();

		for (let i = index - 1; i >= 0; i--) {
			let coin = coins[i];
			if (allowedCoins.includes(coin)) {
				return this.setCoin(coin);
			}
		}
		return false;
	}

	nextCoin() {
		let actualCoin = this.getCoin();
		let coins = this._coins;
		let index = coins.indexOf(actualCoin);
		let allowedCoins = this._getAllowedCoins();

		for (let i = index + 1; i < coins.length; i++) {
			let coin = coins[i];
			if (allowedCoins.includes(coin) && this._canSetCoin(coin)) {
				return coin;
			}
		}
		return null;
	}

	prevCoin() {
		let actualCoin = this.getCoin();
		let coins = this._coins;
		let index = coins.indexOf(actualCoin);
		let allowedCoins = this._getAllowedCoins();

		for (let i = index - 1; i >= 0; i--) {
			let coin = coins[i];
			if (allowedCoins.includes(coin) && this._canSetCoin(coin)) {
				return coin;
			}
		}
		return null;
	}

	incLinesCount() {
		let index = this._lines.indexOf(this.getLinesCount());

		for (let i = index + 1; i < this._lines.length; i++) {
			let linesCount = this._lines[i];
			if (linesCount != null && this._canSetLinesCount(linesCount)) {
				return this.setLinesCount(linesCount);
			}
		}
		return false;
	}

	decLinesCount() {
		let index = this._lines.indexOf(this.getLinesCount());

		for (let i = index - 1; i >= 0; i--) {
			let linesCount = this._lines[i];
			if (linesCount != null && this._canSetLinesCount(linesCount)) {
				return this.setLinesCount(linesCount);
			}
		}
		return false;
	}

	nextLinesCount() {
		let index = this._lines.indexOf(this.getLinesCount());

		for (let i = index + 1; i < this._lines.length; i++) {
			let linesCount = this._lines[i];
			if (linesCount != null && this._canSetLinesCount(linesCount)) {
				return linesCount;
			}
		}
		return false;
	}

	prevLinesCount() {
		let index = this._lines.indexOf(this.getLinesCount());

		for (let i = index - 1; i >= 0; i--) {
			let linesCount = this._lines[i];
			if (linesCount != null && this._canSetLinesCount(linesCount)) {
				return linesCount;
			}
		}
		return false;
	}

	getClosestCoin(bet): number {
		for (let coin of this._coins) {
			for (let atomicBet of this.getCreditBets()) {
				let n = coin * atomicBet;
				if (n === bet) {
					return coin;
				}
			}
		}
		return 100;
	}

	calcMaxBet() {
		let max = 0;
		let res = {
			linesCount: this.getLinesCount(),
			creditBet: this.getCreditBet(),
			coinValue: this.getCoinValue(),
		};

		for (let line of this.getLines()) {
			for (let bet of this.getCreditBets()) {
				for (let coin of this.getCoins()) {
					let maxbet = line * bet * coin;
					if (
						maxbet >= max &&
						this.isBetInBounds(bet * coin, line) &&
						maxbet <= this.getBalance()
					) {
						max = maxbet;

						res.linesCount = line;
						res.creditBet = bet;
						res.coinValue = getCoinValue(coin);
					}
				}
			}
		}

		return res;
	}
}

export class WalletData {
	linesCount: number;
	creditBalance: number;
	creditBetAtomic: number;
	creditBetLines: number;
	// creditAtomicBet: this.getCreditAtomicBet(),
	creditWin: number;
	freespins: number;

	win: number;
	atomicBet: number;

	realBalanceCurrency: string;
	realBetCurrency: string;
	realWinCurrency: string;

	coin: number;
	coinValue: number;
}
